import { BRAND, brandUrl } from '../constants/brand'

const { appName, brandName, brandDescription } = BRAND

/** Marketing copy aligned with App Store Connect listing. */
export const siteCopy = {
  brand: appName,
  tagline: 'Health Companion',
  promo:
    'Daily check-ins, guided lessons, sleep improvement, a full fitness tracker, and water tracking in one calm app. Start with one small step.',
  banner: {
    headline: 'Health companion you can actually use',
    titleHighlight: 'Health Companion',
    body:
      'Daily check-ins, guided lessons, sleep, fitness, and water in one calm app, at your pace.',
    trustStrip:
      'Free to download · Subscribe in the App Store or Google Play · General wellness, not medical advice',
    appStoreUrl: BRAND.appStoreUrl,
    playStoreId: BRAND.bundleId,
    typewriter: [
      'Daily check-ins',
      'Guided lessons',
      'Sleep improvement',
      'Fitness tracking',
      'Water tracking',
    ],
  },
  statsTicker: [
    'Daily check-ins',
    'Guided lessons',
    'Sleep improvement',
    'Fitness tracker',
    'Water tracker',
  ],
  coreModulesSection: {
    badge: 'Core modules',
    title: 'Five modules in one app',
    intro:
      'Check-ins, lessons, sleep, fitness, and water. Each a focused module, together in one calm companion.',
  },
  coreModules: [
    { label: 'Grounding' },
    { label: 'Anxiety Management' },
    { label: 'Depression Resources' },
    { label: 'Move & Restore' },
    { label: 'Accountability' },
    { label: 'Sleep Enhancement' },
  ],
  programSection: {
    badge: 'Core tools',
    title: 'Focused tools in one app',
    intro:
      'Noor centres on the features you use most: mood check-ins, guided lessons, sleep improvement, fitness and water tracking, journaling, and habits.',
  },
  focusedTools: [
    { label: 'Daily check-ins' },
    { label: 'Guided lessons' },
    { label: 'Sleep improvement' },
    { label: 'Fitness tracker' },
    { label: 'Water tracker' },
    { label: 'Journaling' },
    { label: 'Habits' },
  ],
  lookInside: {
    badge: 'Inside the app',
    title: 'Have a look inside',
    intro: 'Tap through check-ins, lessons, toolbox tools, and tracking.',
  },
  homeFeatures: {
    badge: 'What you can do',
    title: 'Focused tools for everyday wellbeing',
    intro:
      'Check in on your mood, learn practical mental health skills, improve sleep, track workouts, and stay hydrated, without clutter.',
    items: [
      {
        title: 'Daily check-ins',
        body: 'Notice how you feel and what is affecting you. Build habits and streaks with gentle accountability.',
      },
      {
        title: 'Guided lessons',
        body: 'Mental health lesson paths grounded in evidence. Regulate, notice, reframe, and take small actions at your pace.',
      },
      {
        title: 'Sleep improvement',
        body: 'Tools and guidance to build better sleep habits, wind down, and improve consistency over time.',
      },
      {
        title: 'Fitness tracker',
        body: 'Log workouts, follow routines, and see progress over time. A full tracker for movement and training, not a side feature.',
      },
      {
        title: 'Water tracker',
        body: 'Set hydration goals, log intake through the day, and build a simple habit that supports how you feel.',
      },
    ],
  },
  whyChoose: {
    badge: 'How lessons work',
    title: 'Structured paths, not random tips',
    intro:
      'Pick a module in Explore, then work through guided lessons at your pace. Every lesson follows the same four-step flow.',
  },
  serviceBlocks: [
    {
      badge: 'Regulate',
      title: 'Settle in first',
      body: 'Each lesson opens with a short step to help you feel ready: orient, breathe, or pause so you can focus on what comes next.',
      bullets: [],
      reverse: false,
      wide: false,
    },
    {
      badge: 'Notice',
      title: 'Name what is going on',
      body: 'Notice thoughts, feelings, and body signals without judgement. See what is affecting you before trying to change it.',
      bullets: [],
      reverse: true,
      wide: false,
    },
    {
      badge: 'Reframe',
      title: 'Shift the pattern',
      body: 'Work through practical ways to challenge unhelpful thinking and see the situation from a clearer angle.',
      bullets: [],
      reverse: false,
      wide: false,
    },
    {
      badge: 'Act',
      title: 'Take one small step',
      body: 'Finish with a concrete action you can try today. Small steps build skills across anxiety, depression, sleep, grounding, and more.',
      bullets: [],
      reverse: true,
      wide: false,
    },
  ],
  premium: {
    title: 'Full app access',
    body: 'Subscribe in the app for full access to every launch module and tool. Plans renew automatically unless cancelled at least 24 hours before the end of the current period. Manage or cancel in your App Store or Google Play subscription settings. Pricing may vary by region.',
  },
  pricing: {
    section: {
      badge: 'Pricing',
      title: 'One subscription, full app',
      intro:
        'Free to download. Subscribe in the app through the App Store or Google Play. No web checkout on this site.',
    },
    trialLabel: '7-day free trial',
    plans: [
      {
        id: 'monthly',
        name: 'Monthly',
        price: '£14.99',
        period: '/month',
        description: 'Full app access, billed monthly.',
        highlighted: false,
        hasTrial: false,
      },
      {
        id: 'annual',
        name: 'Annual',
        price: '£149.90',
        period: '/year',
        description: 'Full app access, billed yearly.',
        highlighted: true,
        hasTrial: true,
      },
    ],
    includesTitle: 'Everything in Noor',
    includes: [
      'Daily check-ins and guided lesson paths',
      'Sleep improvement, fitness, and water tracking',
      'Journaling, habits, and toolbox tools',
      'Explore modules at launch',
    ],
    disclosure:
      'Auto-renewable subscription. Cancel anytime in your App Store or Google Play account settings. Payment is charged to your store account at confirmation of purchase. Prices shown in GBP; local pricing may vary by region.',
    ctaLabel: 'Get the app',
    faqSection: {
      badge: 'Question & Answer',
      title: 'Pricing FAQs',
    },
    faq: [
      {
        id: 'pricingOne',
        question: 'How much does Noor cost?',
        answer:
          'Noor is free to download. Subscribe in the app for £14.99/month or £149.90/year (GBP). Local pricing may vary by region.',
      },
      {
        id: 'pricingTwo',
        question: 'Is there a free trial?',
        answer: 'Yes, 7-day free trial on the annual plan.',
      },
      {
        id: 'pricingThree',
        question: 'Can I subscribe on this website?',
        answer:
          'Subscriptions are only available through the App Store or Google Play. Use the download buttons on this site to install the app and subscribe there.',
      },
      {
        id: 'pricingFour',
        question: 'How do I cancel?',
        answer:
          'Manage or cancel your subscription in your device’s App Store or Google Play subscription settings. Cancellation takes effect at the end of the current billing period.',
      },
    ],
  },
  storeNotify: {
    title: 'Get notified when Noor is on the store',
    body: 'We are not on the App Store or Google Play yet. Leave your email and we will let you know as soon as you can download.',
    placeholder: 'Enter your email',
    submitLabel: 'Notify me',
    success: 'Thanks. We will email you when the app is available to download.',
    error: 'Something went wrong. Please try again or email support@noorhc.app.',
  },
  disclaimer:
    'Noor supports general wellness and self-care. It does not provide medical advice, diagnosis, or treatment. If you are in crisis or need urgent help, contact local emergency services or a qualified professional.',
  footer: {
    tagline: `${appName} is your health companion for everyday mental and physical wellbeing. Check-ins, guided lessons, sleep improvement, fitness tracking, and water tracking in one place.`,
    shortTagline:
      'Daily check-ins, guided lessons, sleep, fitness, and water tracking in one calm app.',
    subscribeBlurb: 'Subscribe for occasional product updates and wellness tips.',
    subscribeLegal: `By subscribing you agree to receive emails from ${appName}. You can unsubscribe at any time.`,
    copyright: `© ${new Date().getFullYear()} ${brandName}. All rights reserved.`,
    supportEmail: BRAND.email.support,
    websiteUrl: brandUrl('marketing'),
  },
  contact: {
    title: 'Privacy and support',
    intro: `Questions or account help for ${appName}? Submit the form below or email ${BRAND.email.support}. See Privacy Policy and Terms of Use in the app.`,
  },
  homeCall: {
    title: 'Need support?',
    intro: [
      `Questions about ${appName}, your account, or privacy?`,
      'We are here to help.',
    ],
    contactCtaLabel: 'Contact us',
    contactCtaTo: '/contact',
    emailCtaLabel: 'Email support',
  },
  features: {
    breadcrumbHeading: 'Features',
    breadcrumbTitle: `${appName} features`,
    breadcrumbDescription:
      'Daily check-ins, guided lessons, sleep improvement, a full fitness tracker, and water tracking in one calm app.',
  },
  aboutUs: {
    overview: {
      badge: 'Overview',
      title: `Meet ${appName}, your everyday health companion`,
      body: `${appName} brings together the tools people use most for mental and physical wellbeing: daily check-ins, guided lessons, sleep improvement, a full fitness tracker, and water tracking. Everything lives in one calm app, designed for small steps rather than overwhelming overhauls.`,
      bullets: [
        'Daily check-ins to notice mood and what affects you',
        'Guided lesson paths',
        'Sleep improvement tools you can act on',
        'A dedicated fitness tracker for workouts and routines',
        'Water tracking with daily goals and quick logging',
      ],
      ctaLabel: 'Explore features',
      ctaTo: '/features',
    },
    highlights: {
      badge: 'What we focus on',
      title: 'Practical tools, not filler',
      intro:
        'We built Noor around five core capabilities so you spend time on what helps, not browsing endless catalogues.',
      items: [
        { label: '5', suffix: '', caption: 'Focused core tools' },
        { label: '1', suffix: '', caption: 'Calm app experience' },
        { label: '24/7', suffix: '', caption: 'Available when you need it' },
        { label: 'You', suffix: '', caption: 'Set the pace' },
      ],
    },
    whoWeAre: {
      badge: 'Who we are',
      title: `Wellbeing support that fits real life`,
      body: `${appName} comes from ${brandName}. We believe everyday mental and physical health should feel approachable: practical skills, gentle accountability, and tools that work together. ${appName} supports general wellness and self-care. It does not provide medical advice, diagnosis, or treatment. If you are in crisis or need urgent help, contact local emergency services or a qualified professional.`,
    },
    mission: {
      badge: 'Our approach',
      title: 'Evidence-informed, calm, and at your pace',
      body: 'Lesson content is designed to be practical and grounded in evidence. Check-ins, sleep, fitness, and hydration tools are meant to reinforce small, repeatable habits. We aim for encouragement without pressure, so progress feels sustainable.',
      bullets: [
        'Mental health lessons you can replay anytime',
        'Fitness tracking with routines, logs, and progress',
        'Sleep improvement tied to habits and consistency',
        'Full app access with a simple monthly or annual subscription',
      ],
      ctaLabel: 'Contact us',
      ctaTo: '/contact',
    },
    tickerBadge: 'What Noor includes',
    principles: {
      badge: 'How we build',
      title: 'Principles behind the app',
      intro: 'Every feature on this site reflects how we think about everyday wellbeing.',
      items: [
        {
          title: 'Evidence-informed',
          body: 'Lesson paths draw on established approaches to mental health skills, presented in plain language you can use straight away.',
        },
        {
          title: 'Focused by design',
          body: 'Five core tools instead of endless categories. Check-ins, lessons, sleep, fitness, and water tracking stay front and centre.',
        },
        {
          title: 'Your pace',
          body: 'Replay lessons, log a workout, or skip a day without judgement. Small steps and consistency matter more than perfection.',
        },
      ],
    },
    faq: {
      badge: 'Question & Answer',
      title: 'FAQs',
      items: [
        {
          id: 'collapseOne',
          heading: 'headingOne',
          question: `What is ${appName}?`,
          answer: `${appName} is a health companion app for everyday mental and physical wellbeing. It includes daily check-ins, guided lessons, sleep improvement tools, a fitness tracker, and water tracking in one place.`,
        },
        {
          id: 'collapseTwo',
          heading: 'headingTwo',
          question: 'Can Noor replace therapy or medical care?',
          answer:
            'No. Noor supports general wellness and self-care. It does not provide medical advice, diagnosis, or treatment. If you are in crisis or need urgent help, contact local emergency services or a qualified professional.',
        },
        {
          id: 'collapseThree',
          heading: 'headingThree',
          question: 'How much does Noor cost?',
          answer:
            'Noor is free to download. Subscribe in the app for £14.99/month or £149.90/year (GBP). The annual plan includes a 7-day free trial. Local pricing may vary by region. Manage your subscription in the App Store or Google Play.',
        },
        {
          id: 'collapseFour',
          heading: 'headingFour',
          question: 'How do I get help with my account?',
          answer: `Email us at ${BRAND.email.support} or use the contact form on this site. For privacy and terms, see the Privacy Policy and Terms of Use linked in the footer.`,
        },
        {
          id: 'collapseFive',
          heading: 'headingFive',
          question: 'Where can I download the app?',
          answer:
            'Search for "Noor" on the App Store or Google Play, or use the download buttons on this site.',
        },
        {
          id: 'collapseSix',
          heading: 'headingSix',
          question: 'Who operates Noor?',
          answer: `${appName} comes from ${brandName}. ${brandDescription}`,
        },
      ],
    },
    download: {
      badge: 'Download',
      title: 'Take your next small step today',
    },
  },
}
